import {
  Component,
  EventEmitter,
  Inject,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { ButtonComponent } from '../button';
import {
  ReactiveFormsModule,
  FormsModule,
  FormGroup,
  Validators,
  FormBuilder,
} from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { IconComponent } from '../icon';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { TranslatorService } from '@sc/data-access-translation';
import { FeatureFlippingManagerService } from '@sc/feature-flipping';
import { MatDialogTitle } from '@angular/material/dialog';
import { NgForOf, NgIf } from '@angular/common';
import { ConfigurationService } from '@sc/data-access-configuration';
import { CLIENT_APP_ID, ClientAppId } from '@sc/data-access-models';
import { MatCheckbox } from '@angular/material/checkbox';

@Component({
  selector: 'sc-cancel-appointment-sheet',
  template: `
    <div class="cancel-appointment-container">
      <div class="close">
        <button type="button" class="close-btn" (click)="onQuit()">
          <span class="sr_only">{{
            'GlobalApplicativeConstants.accessibility.closeDialog'
              | translate
                : {
                    dialogTitle: 'CancelAppointmentSheet.mainTitle' | translate
                  }
          }}</span>
          <sc-icon name="close"></sc-icon>
        </button>
      </div>
      <h1
        mat-dialog-title
        id="cancel-appointment-title-id"
        [innerHTML]="'CancelAppointmentSheet.mainTitle' | translate"
      ></h1>
      <h2>{{ 'CancelAppointmentSheet.subTitle' | translate }}</h2>
      <form [formGroup]="form">
        <mat-radio-group
          formControlName="reasonChoice"
          *ngIf="showCancelReason()"
        >
          <mat-radio-button
            *ngFor="let reason of reasonChoices"
            [value]="reason"
            [checked]="form?.get('reasonChoice')?.value === reason"
            >{{
              'CancelAppointmentSheet.reasonChoices.' +
                clientAppId +
                '.' +
                reason | translate
            }}</mat-radio-button
          >
          <mat-radio-button
            value="other"
            [checked]="form?.get('reasonChoice')?.value === 'reason'"
            >{{
              'CancelAppointmentSheet.reasonChoices.' + clientAppId + '.other'
                | translate
            }}</mat-radio-button
          >
        </mat-radio-group>
        <textarea
          *ngIf="otherReason"
          matInput
          #cancelCommentText
          [placeholder]="'NoShowComment.textareaPlaceHolder' | translate"
          formControlName="specificReason"
        ></textarea>
      </form>
      <mat-checkbox [(ngModel)]="notifyClient" id="notifyClientCheckbox">{{
        'CancelAppointmentSheet.notifyClientCheckbox' | translate
      }}</mat-checkbox>
      <sc-button
        id="acceptBtn"
        buttonType="error"
        [disabled]="!form.valid"
        (click)="confirmCancelClick()"
        >{{ 'CancelAppointmentSheet.acceptButton' | translate }}</sc-button
      >

      <sc-button id="cancelBtn" buttonType="secondary" (click)="onQuit()">{{
        'CancelAppointmentSheet.cancelButton' | translate
      }}</sc-button>
    </div>
  `,
  styleUrls: ['./cancel-appointment-sheet.component.scss'],
  encapsulation: ViewEncapsulation.None,
  imports: [
    IconComponent,
    MatInputModule,
    ReactiveFormsModule,
    FormsModule,
    ButtonComponent,
    TranslateModule,
    MatRadioButton,
    MatRadioGroup,
    MatDialogTitle,
    NgIf,
    NgForOf,
    MatCheckbox,
  ],
})
export class CancelAppointmentSheetComponent implements OnInit {
  @Output() quit = new EventEmitter<void>();
  @Output() acceptCancel = new EventEmitter<{
    comment: string;
    notifyClient: boolean;
  }>();

  form: FormGroup;
  reasonChoices: string[];
  otherReason: boolean;
  notifyClient = true;

  constructor(
    private readonly translatorService: TranslatorService,
    private readonly fb: FormBuilder,
    public featureFlippingService: FeatureFlippingManagerService,
    private readonly configService: ConfigurationService,
    @Inject(CLIENT_APP_ID) public clientAppId: ClientAppId
  ) {}

  ngOnInit() {
    this.form = this.showCancelReason()
      ? this.fb.group({
          reasonChoice: [null, Validators.required],
          specificReason: [
            { value: '', disabled: true },
            Validators.maxLength(300),
          ],
        })
      : this.fb.group({
          specificReason: ['', Validators.maxLength(300)],
        });

    this.form.get('reasonChoice')?.valueChanges?.subscribe((value) => {
      if (value === 'other') {
        this.form?.get('specificReason')?.enable();
      } else {
        this.form?.get('specificReason')?.disable();
      }

      this.form.updateValueAndValidity();
    });

    this.reasonChoices =
      this.configService.getConfig().cancelComment?.reasons || [];
    this.otherReason = this.showCancelReason()
      ? this.configService.getConfig().cancelComment?.allowOther
      : true;
  }

  showCancelReason() {
    return this.featureFlippingService.isFeatureActivated(
      'appointment-canceling-reason'
    );
  }

  canValidate() {
    if (!this.form.valid) {
      return false;
    }

    const specificReason = this.form.get('specificReason')?.value?.trim();
    return !(
      this.form.get('reasonChoice')?.value === 'other' &&
      (!specificReason || specificReason.length === 0)
    );
  }

  confirmCancelClick() {
    if (this.showCancelReason() && !this.canValidate()) {
      return;
    }
    const reasonCode = this.form.get('reasonChoice')?.value;
    let comment: string;
    if (reasonCode === 'other' || !this.showCancelReason()) {
      comment = this.form.get('specificReason')?.value;
    } else {
      comment = this.translatorService.getInstantTranslation(
        `CancelAppointmentSheet.reasonChoices.${this.clientAppId}.${reasonCode}`
      );
    }
    this.acceptCancel.emit({ comment, notifyClient: this.notifyClient });
  }

  onQuit() {
    this.quit.emit();
  }
}
